import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { AddEditComponent } from '@shared/components/drdp-tables/add-edit/add-edit.component';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  AbstractControl,
} from '@angular/forms';
import { LookupService } from '@core/services/lookup.service';
import { ToastService } from '@core/services/toast.service';
import { AuthService } from '@core/services/auth.service';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { UserService } from '@core/services/user.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { ITraining } from '@core/interfaces/itraining';
import { Trainings } from '@core/enums/trainings';
import { toggleSelectionWithNone } from '@core/services/helper.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Permission } from '@core/enums/permissions';
import { PermissionService } from '@core/services/permission.service';

@Component({
    selector: 'drdp-edit-user-profile',
    templateUrl: './edit-user-profile.component.html',
    styleUrls: ['./edit-user-profile.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditUserProfileComponent implements OnInit {
  editUserForm: FormGroup | any;
  submitAttempt = false;
  private subscriptions = new Subscription();

  @ViewChild(AddEditComponent) table?: AddEditComponent;
  dropDownForm: FormGroup | any;
  user: any;
  userInfo = {} as ILoginResponse;
  trainings: ITraining[] = [];
  canEditUserProfileAgencyData = false;

  constructor(
    public lookup: LookupService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private userService: UserService,
    private updateUserService: UserService,
    private toast: ToastService,
    private fb: FormBuilder,
    private router: Router,
    private translocoService: TranslocoService
  ) {}

  get emailName() {
    return this.editUserForm.get('emailName');
  }
  get firstName() {
    return this.editUserForm.get('firstName');
  }
  get lastName() {
    return this.editUserForm.get('lastName');
  }
  get middleName() {
    return this.editUserForm.get('middleName');
  }
  get jobTitle() {
    return this.editUserForm.get('jobTitle');
  }
  get suffix() {
    return this.editUserForm.get('suffix');
  }
  get trainingIds() {
    return this.editUserForm.get('trainingIds');
  }
  get isCecoCertified() {
    return this.editUserForm.get('isCecoCertified');
  }
  get isAgencyRatingViewLocked() {
    return this.editUserForm.get('isAgencyRatingViewLocked');
  }
  get isAgencyEulaSubmitted() {
    return this.editUserForm.get('isAgencyEulaSubmitted');
  }
  ngOnInit(): void {
    this.canEditUserProfileAgencyData = this.permissionService.checkPermission(Permission.EditUserProfileAgencyData);
    this.initializeForms();
    this.getTrainings();
    this.getUserData();
  }

  initializeForms() {
    this.editUserForm = this.fb.group({
      emailName: [
        '',
        [Validators.email, Validators.required, Validators.maxLength(100)],
      ],
      firstName: ['', [Validators.required, Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.maxLength(50)]],
      middleName: ['', [Validators.maxLength(1)]],
      jobTitle: [''],
      suffix: [''],
      trainingIds: [[], [Validators.required]],
      isCecoCertified: [null, [Validators.required]],
      isAgencyRatingViewLocked: [null, this.canEditUserProfileAgencyData ? [Validators.required] : []],
      isAgencyEulaSubmitted: [null, this.canEditUserProfileAgencyData ? [Validators.required] : []],
    });
  }

  getTrainings() {
    this.subscriptions.add(
      this.lookup.getTrainings().subscribe((res: ITraining[]) => {
        this.trainings = res;
      })
    );
  }

  isTrainingSelected(trainingId: number): boolean {
    return this.trainingIds.value.includes(trainingId);
  }

  handleTraining(event: MatCheckboxChange, trainingId: number) {
    const updated = toggleSelectionWithNone(
      this.trainingIds.value,
      trainingId,
      event.checked,
      Trainings.NoTrainingsCompleted
    );

    // Selection was rejected, so revert the checkbox
    if (event.checked && !updated.includes(trainingId)) {
      event.source.checked = false;
    }

    this.trainingIds.setValue(updated);
    this.trainingIds.markAsTouched();
  }

  invalid(control: AbstractControl) {
    if (
      control === undefined ||
      control == null ||
      typeof control === 'undefined'
    ) {
      return false;
    } else {
      return (
        (!control.valid && control.touched) ||
        (!control.valid && control.untouched && this.submitAttempt)
      );
    }
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  getUserData() {
    this.subscriptions.add(
      this.userService
        .getUserById(this.authService.getCurrentUser().userId)
        .subscribe((res) => {
          this.user = res;
          this.editUserForm.controls['firstName'].setValue(this.user.firstName);
          this.editUserForm.controls['emailName'].setValue(this.user.emailName);
          this.editUserForm.controls['middleName'].setValue(
            this.user.middleName
          );
          this.editUserForm.controls['lastName'].setValue(this.user.lastName);
          this.editUserForm.controls['jobTitle'].setValue(this.user.jobTitle);
          this.editUserForm.controls['suffix'].setValue(this.user.suffix);
          this.editUserForm.controls['trainingIds'].setValue(this.user.trainingIds ?? []);
          this.editUserForm.controls['isCecoCertified'].setValue(this.user.isCecoCertified ?? null);
          this.editUserForm.controls['isAgencyRatingViewLocked'].setValue(this.user.isAgencyRatingViewLocked ?? null);
          this.editUserForm.controls['isAgencyEulaSubmitted'].setValue(this.user.isAgencyEulaSubmitted ?? null);
        })
    );
  }

  handleSuffix(event: any) {
    this.suffix.setValue(event?.name);
  }

  onSubmit() {
    if (this.editUserForm.invalid) {
      this.validateAllFormFields(this.editUserForm);
      this.toast.error(this.translocoService.translate('user.userProfile.edit.toast.incomplete'));
      return;
    }
  
    this.submitAttempt = true;
  
    const updateUserInfo = {
      UserId: this.authService.getCurrentUser().userId,
      FirstName: this.editUserForm.get('firstName').value,
      EmailName: this.editUserForm.get('emailName').value,
      LastName: this.editUserForm.get('lastName').value,
      MiddleName: this.editUserForm.get('middleName').value,
      JobTitle: this.editUserForm.get('jobTitle').value,
      Suffix: this.editUserForm.get('suffix').value,
      TrainingIds: this.trainingIds.value,
      IsCecoCertified: this.isCecoCertified.value,
      IsAgencyRatingViewLocked: this.canEditUserProfileAgencyData ? this.isAgencyRatingViewLocked.value : null,
      IsAgencyEulaSubmitted: this.canEditUserProfileAgencyData ? this.isAgencyEulaSubmitted.value : null,
    };
  
    this.subscriptions.add(
      this.updateUserService.updateUserDemographics(updateUserInfo)
        .subscribe((success: boolean) => {
          if (success) {
            this.userService.markProfileComplete(updateUserInfo.UserId);
            this.toast.success(this.translocoService.translate('user.userProfile.edit.toast.updated'));
            this.router.navigateByUrl('/user-profile');
          }
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
